"use client";

import { useEffect } from "react";
import "vanilla-cookieconsent/dist/cookieconsent.css";
import * as CookieConsent from "vanilla-cookieconsent";
import { useIframeManager } from "@/hooks/useIframeManager";
import type { CookieConsentConfig } from "vanilla-cookieconsent";
import { CLOUDFLARE_BEACON } from "@/lib/constants";

export default function CookieConsentBanner() {
  // Initialize IframeManager with route change handling
  useIframeManager();

  // Handle dark mode
  useEffect(() => {
    const darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const updateDarkMode = (e: MediaQueryListEvent | MediaQueryList) => {
      if (e.matches) {
        document.documentElement.classList.add('cc--darkmode');
      } else {
        document.documentElement.classList.remove('cc--darkmode');
      }
    };

    // Initial check
    updateDarkMode(darkModeMediaQuery);

    // Listen for changes
    darkModeMediaQuery.addEventListener('change', updateDarkMode);

    return () => darkModeMediaQuery.removeEventListener('change', updateDarkMode);
  }, []);

  // Initialize cookie consent
  useEffect(() => {
    if (CookieConsent.validCookie("cookie_consent")) return;

    let im: ReturnType<typeof window.iframemanager> | undefined;

    // Get iframemanager instance if available
    if (
      typeof window !== "undefined" &&
      typeof window.iframemanager === "function"
    ) {
      im = window.iframemanager();
    }

    const config: CookieConsentConfig = {
      revision: 2,
      guiOptions: {
        consentModal: {
          layout: "bar inline",
          position: "bottom",
          equalWeightButtons: true,
          flipButtons: true,
        },
        preferencesModal: {
          layout: "box",
          position: "right",
          equalWeightButtons: true,
          flipButtons: true,
        },
      },
      onConsent: ({ }) => {
        if (CookieConsent.acceptedCategory("analytics")) {
          // Get the current domain and port
          const domain = typeof window !== 'undefined' ? window.location.hostname : '';

          if (domain === 'gengo-kotatsu.com' || domain === 'www.gengo-kotatsu.com') {
            // Create and inject the Cloudflare analytics script
            const script = document.createElement('script');
            script.defer = true;
            script.src = 'https://static.cloudflareinsights.com/beacon.min.js';
            script.setAttribute('data-cf-beacon', `{"token": "${CLOUDFLARE_BEACON}"}`);
            document.body.appendChild(script);
          } else {
            console.log('Skipping analytics: not a production domain');
          }
        }
      },
      categories: {
        necessary: {
          enabled: true,
          readOnly: true,
          services: {
            algoliaSearch: {
              label: "Algolia Search",
            },
          }
        },
        third: {
          enabled: false,
          readOnly: false,
          services: {
            youtube: {
              label: "YouTube",
              onAccept: () => {
                im?.acceptService("youtube");
              },
              onReject: () => {
                im?.rejectService("youtube");
              },
            },
            spotify: {
              label: "Spotify",
              onAccept: () => {
                im?.acceptService("spotify");
              },
              onReject: () => {
                im?.rejectService("spotify");
              },
            },
            gcal: {
              label: "Google Calendar",
              onAccept: () => {
                im?.acceptService("gcal");
              },
              onReject: () => {
                im?.rejectService("gcal");
              },
            },
            gmaps: {
              label: "Google Maps",
              onAccept: () => {
                im?.acceptService("gmaps");
              },
              onReject: () => {
                im?.rejectService("gmaps");
              },
            },
          },
        },
        analytics: {
          enabled: false,
          readOnly: false,
          services: {
            cloudflareInsights: {
              label: "Cloudflare Insights",
            },
          },
        },
      },
      language: {
        autoDetect: "browser",
        default: "ja",
        translations: {
          ja: {
            consentModal: {
              title: "プライバシー設定",
              description:
                "当サイトでは、YouTube、Google カレンダー、Google マップ、Spotifyなどの外部サービスを使用しています。これらのサービスは、データを外部プロバイダーに送信する可能性があります。どのカテゴリーを許可するか選択できます。",
              acceptAllBtn: "すべて許可",
              acceptNecessaryBtn: "必要なもののみ",
              showPreferencesBtn: "設定を変更",
            },
            preferencesModal: {
              title: "プライバシー設定",
              acceptAllBtn: "すべて許可",
              acceptNecessaryBtn: "必要なもののみ",
              savePreferencesBtn: "設定を保存",
              closeIconLabel: "閉じる",
              sections: [
                {
                  title: "必須の機能",
                  description:
                    "これらの機能は、ウェブサイトの基本的な技術機能を確保するために必要不可欠です。これらを無効にすることはできません。 Algoliaを使用して高度な検索機能を提供しています。検索機能を使用すると、検索クエリなどのデータがAlgoliaに送信される可能性があります。",
                  linkedCategory: "necessary",
                },
                {
                  title: "外部サービス",
                  description:
                    "この項目には、YouTube動画、Spotifyプレイヤー、Google マップ、Google カレンダーが含まれます。同意後、これらのコンテンツは外部サーバーから読み込まれます。プロバイダーはあなたの利用状況に関する情報を収集する可能性があります。",
                  linkedCategory: "third",
                },
                {
                  title: "分析と性能",
                  description:
                    "Cloudflare Insightsを使用して、読み込み時間やエラーレポートなどの技術データを分析し、ウェブサイトのパフォーマンスを向上させています。",
                  linkedCategory: "analytics",
                },
              ],
            },
          },
          de: {
            consentModal: {
              title: "Datenschutz-Einstellungen",
              description:
                "Wir verwenden externe Dienste wie YouTube, Google Kalender und Spotify, die Daten an externe Anbieter übermitteln können. Sie können wählen, welche Kategorien Sie akzeptieren möchten.",
              acceptAllBtn: "Alle akzeptieren",
              acceptNecessaryBtn: "Nur notwendige akzeptieren",
              showPreferencesBtn: "Einstellungen anpassen",
            },
            preferencesModal: {
              title: "Datenschutzeinstellungen",
              acceptAllBtn: "Alle akzeptieren",
              acceptNecessaryBtn: "Nur notwendige akzeptieren",
              savePreferencesBtn: "Einstellungen speichern",
              closeIconLabel: "Schließen",
              sections: [
                {
                  title: "Notwendige Funktionen",
                  description:
                    "Diese Funktionen sind erforderlich, um die grundlegenden technischen Funktionen der Website sicherzustellen. Sie können nicht deaktiviert werden. Unsere Website-Suche wird durch Algolia bereitgestellt. Bei Nutzung können Daten wie Ihre Suchanfragen an Algolia übermittelt werden.",
                  linkedCategory: "necessary",
                },
                {
                  title: "Externe Dienste",
                  description:
                    "Diese Kategorie umfasst YouTube-Videos, Spotify-Player, Google Maps und Google Kalender. Nach Ihrer Zustimmung werden diese Inhalte von externen Servern geladen. Die Anbieter können dabei Informationen über Ihre Nutzung sammeln.",
                  linkedCategory: "third",
                },
                {
                  title: "Analyse und Leistung",
                  description:
                    "Wir verwenden Cloudflare Insights, um technische Daten wie Ladezeiten und Fehlerberichte zu analysieren und die Leistung unserer Website zu verbessern.",
                  linkedCategory: "analytics",
                },
              ],
            },
          },
        },
      },
    };

    // Initialize CookieConsent
    CookieConsent.run(config);
  }, []);

  return null;
}
